import * as PIXI from 'pixi.js';

/**
 * PingEffectLayer
 *
 * Simple pulse wave at a world position — an expanding gold ring
 * with soft glow fill and a trailing echo ring. Clean and organic.
 *
 * Multiple pings can be active simultaneously.
 * Each auto-removes after the duration completes.
 */

interface ActivePing {
  x: number;
  y: number;
  elapsed: number;
  graphics: PIXI.Graphics;
}

const PING_DURATION = 1.2; // seconds
const MAX_RADIUS = 80; // pixels
const GOLD = 0xd4af37;

export class PingEffectLayer {
  public container: PIXI.Container;
  private pings: ActivePing[] = [];
  private ticker: PIXI.Ticker | null = null;

  constructor() {
    this.container = new PIXI.Container();
    this.container.zIndex = 999; // above everything
  }

  setTicker(ticker: PIXI.Ticker): void {
    this.ticker = ticker;
    this.ticker.add(this.update, this);
  }

  ping(x: number, y: number): void {
    const graphics = new PIXI.Graphics();
    this.container.addChild(graphics);
    const ping: ActivePing = { x, y, elapsed: 0, graphics };
    this.pings.push(ping);
    this.drawPing(ping); // draw immediately so first frame is visible
  }

  private update = (): void => {
    if (this.pings.length === 0) return;
    const dt = this.ticker ? this.ticker.deltaMS / 1000 : 1 / 60;

    for (let i = this.pings.length - 1; i >= 0; i--) {
      const p = this.pings[i];
      p.elapsed += dt;

      if (p.elapsed >= PING_DURATION) {
        this.container.removeChild(p.graphics);
        p.graphics.destroy();
        this.pings.splice(i, 1);
        continue;
      }

      this.drawPing(p);
    }
  };

  private drawPing(p: ActivePing): void {
    const g = p.graphics;
    g.clear();

    const t = p.elapsed / PING_DURATION; // 0 → 1

    // Ease-out expansion — fast start, gentle finish
    const eased = 1 - Math.pow(1 - t, 3);
    const radius = eased * MAX_RADIUS;

    // Fade: bright at start, fades to nothing
    const fade = Math.pow(1 - t, 2);

    // Soft inner glow fill — fades and shrinks
    if (fade > 0.05) {
      g.lineStyle(0);
      g.beginFill(GOLD, fade * 0.12);
      g.drawCircle(p.x, p.y, radius * 0.6);
      g.endFill();
    }

    // Main pulse ring — expanding outward
    const ringWidth = Math.max(1, 4 * (1 - t));
    g.lineStyle(ringWidth, GOLD, fade * 0.8);
    g.drawCircle(p.x, p.y, radius);

    // Thin outer echo ring — slightly ahead, softer
    if (t > 0.1) {
      const echoT = Math.min(1, (t - 0.1) / 0.9);
      const echoEased = 1 - Math.pow(1 - echoT, 3);
      const echoRadius = echoEased * MAX_RADIUS * 1.15;
      const echoFade = Math.pow(1 - echoT, 2.5);
      g.lineStyle(Math.max(0.5, 1.5 * (1 - echoT)), GOLD, echoFade * 0.3);
      g.drawCircle(p.x, p.y, echoRadius);
    }

    // Center dot — bright, fades quickly
    const dotFade = Math.pow(1 - t, 4);
    if (dotFade > 0.05) {
      g.lineStyle(0);
      g.beginFill(GOLD, dotFade * 0.9);
      g.drawCircle(p.x, p.y, 3 * (1 - t * 0.5));
      g.endFill();
    }
  }

  destroy(): void {
    if (this.ticker) {
      this.ticker.remove(this.update, this);
      this.ticker = null;
    }
    for (const p of this.pings) {
      p.graphics.destroy();
    }
    this.pings = [];
    this.container.destroy({ children: true });
  }
}
